import { useEffect, useState } from 'react';
import { purchasingAPI, qualityAPI, PurchaseOrderLine, NonConformanceReport } from '../../lib/api';
import { ChangelogEntry } from '../common/Changelog';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface UsePOLineDetailArgs {
  lineId: number;
  isOpen: boolean;
  userType: 'customer' | 'supplier';
  /** The authenticated user, for the platform checks in the visibility rules. */
  user: { platform?: string } | null | undefined;
  /** True when the tenant negotiates on schedule rows instead of the PO line. */
  scheduleCollaborationActive: boolean;
  /** Site-scoped write check from UserPermissionsContext. */
  hasPurchasingWriteAccess: (siteId?: number) => boolean;
  onUpdate?: () => void;
}

/**
 * Everything the PO Line Detail modal does, as opposed to everything it draws.
 *
 * Lifted out of POLineDetailModal verbatim to bring that file back under the
 * 500-line limit (CR-7258 touched it, so the limit applies). It owns the line,
 * its changelog, the GRN count and the load state, because every function here
 * reads or writes them; the modal keeps only its own presentational state.
 *
 * @returns The loaded line and changelog, plus the workflow actions and the
 *   two visibility predicates the header and footer consult.
 */
export function usePOLineDetail({
  lineId,
  isOpen,
  userType,
  user,
  scheduleCollaborationActive,
  hasPurchasingWriteAccess,
  onUpdate,
}: UsePOLineDetailArgs) {
  const [line, setLine] = useState<PurchaseOrderLine | null>(null);
  const [changelogs, setChangelogs] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // CR-4699: GRN count for tab label
  const [grnCount, setGrnCount] = useState<number>(0);
  // Customer review 11 Sep (item 30a/b): the line's NCRs, from the same
  // role-scoped endpoint the NCR tab lists — drives the "NCR (n)" tab label
  // and the NCR entries in the Audit History. Null = could not be loaded.
  const [ncrs, setNcrs] = useState<NonConformanceReport[] | null>(null);

  useEffect(() => {
    if (isOpen && lineId) {
      fetchLineDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lineId]);

  interface RawChangelog {
    id: number; change_date: string; field_changed: string; old_value: string; new_value: string;
    changed_by_details?: { full_name?: string; companyName?: string };
    reason?: string; change_type?: string; event_type?: string;
    // CR-4712 round 2 rework: previous version of this transformer read
    // `changed_by_company_name` (with the _name suffix) and
    // `changed_by_details.companyName` — neither field exists on the
    // backend payload. The serializer returns `changed_by_company`
    // (resolved string from get_changed_by_company) directly. The
    // mismatched key meant this transformer always produced
    // `changed_by_company: undefined`, the Changelog row renderer fell
    // through its `entry.changed_by_company ? ... : ''` ternary, and
    // the audit row rendered as a bare actor name in production —
    // exactly the regression Sarah reported. Read the actual field.
    changed_by_company?: string;
    // CR-4713 round 2 rework: surface the impersonation actor + their
    // company so the Changelog row renderer can append the
    // "(acting as <Company> · <Name>)" suffix.
    acting_as_user_details?: { id: number; first_name?: string; last_name?: string; full_name?: string; email?: string } | null;
    acting_as_company?: string | null;
    // CR-4865: carry the change-request grouping key through to the tab renderer.
    change_request_ref?: number | null;
    po_line?: number | null;
    // CR-4713 round 4: the renderer needs this to say "on behalf of supplier/customer".
    company_type?: 'customer' | 'supplier' | '';
  }
  const transformChangelogs = (results: RawChangelog[]): ChangelogEntry[] =>
    results.map((log) => ({
      id: log.id, change_date: log.change_date,
      changed_by: log.changed_by_details?.full_name || 'System',
      changed_by_company: log.changed_by_company,
      acting_as_user_details: log.acting_as_user_details,
      acting_as_company: log.acting_as_company,
      field_changed: log.field_changed, old_value: log.old_value, new_value: log.new_value,
      reason: log.reason, change_type: log.change_type, event_type: log.event_type,
      change_request_ref: log.change_request_ref ?? null,
      company_type: log.company_type,
    }));

  const fetchLineDetails = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const [lineResponse, changelogResponse, grnResponse, ncrResponse] = await Promise.all([
        purchasingAPI.getLine(lineId),
        purchasingAPI.getChangelogs({ po_line: lineId, ordering: '-change_date' }),
        purchasingAPI.getLineGRNSummary(lineId),
        // An NCR failure (e.g. no quality access) must not take the whole
        // modal down; the tab label just loses its count.
        qualityAPI.getNCRs({ po_line: lineId, page_size: 50 }).catch((ncrErr: unknown) => {
          console.warn('Could not load NCRs for the line:', ncrErr);
          return null;
        }),
      ]);

      setLine(lineResponse.data);
      setChangelogs(transformChangelogs(changelogResponse.data.results));
      // CR-4699: Store GRN count for tab label
      setGrnCount(grnResponse.data.total_grns);
      setNcrs(ncrResponse ? ncrResponse.data.results || [] : null);
    } catch (err: unknown) {
      console.error('Failed to fetch line details:', err);
      if (!silent) {
        const axiosError = err as { response?: { data?: { detail?: string } } };
        setError(axiosError.response?.data?.detail || 'Failed to load line details');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const getChangeCount = (fieldName: string) => {
    // CR-4731 round 4: a change-request lifecycle writes both a propose row
    // and a decision row on the same field (post-CR-4865 they share a
    // change_request_ref; pre-CR-4865 legacy rows don't). A naive .length
    // double-counts. Group by ref for modern rows; for legacy rows without a
    // ref, skip ones that look like decisions so a propose + decision pair
    // still resolves to one change.
    const isDecisionRow = (reason?: string | null) =>
      /\b(accepted|declined|rejected|cancelled)\b/i.test(reason || '');
    const seenRefs = new Set<number>();
    let count = 0;
    changelogs.forEach(log => {
      if (log.field_changed !== fieldName) return;
      if (log.change_request_ref != null) {
        if (!seenRefs.has(log.change_request_ref)) {
          seenRefs.add(log.change_request_ref);
          count++;
        }
      } else if (!isDecisionRow(log.reason)) {
        count++;
      }
    });
    return count > 0 ? count : null;
  };

  const handleToggleCritical = async () => {
    if (!line) return;
    try {
      const newCriticalState = !line.is_critical;
      const response = await purchasingAPI.setLineCritical(line.id, newCriticalState);
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setLine(prev => prev ? { ...prev, is_critical: newCriticalState } : null);
        const changelogResponse = await purchasingAPI.getChangelogs({ po_line: lineId, ordering: '-change_date' });
        setChangelogs(transformChangelogs(changelogResponse.data.results));
        onUpdate?.();
      }
    } catch (error: unknown) {
      console.error('Failed to toggle critical:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update critical status';
      toast.error(errorMessage);
    }
  };

  const handleToggleHold = async (reason?: string) => {
    if (!line) return;
    try {
      let response;
      if (line.on_hold) {
        response = await purchasingAPI.releaseLineHold(line.id);
      } else {
        if (!reason) return;
        response = await purchasingAPI.putLineOnHold(line.id, { reason });
      }
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        await fetchLineDetails();
        onUpdate?.();
      }
    } catch (err: unknown) {
      console.error('Failed to toggle hold:', err);
      const axiosError = err as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Failed to update hold status');
    }
  };

  const handleWorkflowAction = async (data: {
    action: string;
    lineId: number;
    comments?: string;
    reason?: string;
    proposedDate?: string;
    proposedQuantity?: number;
    proposedPrice?: number;
    files?: File[];
  }) => {
    try {
      let response;

      switch (data.action) {
        case 'accept':
          response = await purchasingAPI.acceptDeliveryDate(data.lineId, { comments: data.comments });
          break;
        case 'decline':
          response = await purchasingAPI.declineDeliveryDate(data.lineId, { reason: data.reason ?? '', comments: data.comments });
          break;
        case 'propose':
          response = await purchasingAPI.proposeAlternativeDate(data.lineId, { proposedDate: data.proposedDate ?? '', reason: data.reason ?? '', comments: data.comments });
          break;
        case 'proposeQuantity':
          response = await purchasingAPI.proposeQuantityChange(data.lineId, { proposedQuantity: String(data.proposedQuantity ?? 0), reason: data.reason ?? '', comments: data.comments });
          break;
        case 'proposePrice':
          response = await purchasingAPI.proposePriceChange(data.lineId, { proposedPrice: String(data.proposedPrice ?? 0), reason: data.reason ?? '', comments: data.comments });
          break;
        default:
          throw new Error(`Unknown action: ${data.action}`);
      }

      if (response?.data?.message) {
        toast.success(response.data.message);
      }

      // CR-4723: Upload attached files as PO documents after action succeeds.
      // Use other_supplier / other_customer — PODocument has no change_request choice.
      if (data.files && data.files.length > 0 && line) {
        const docType = userType === 'supplier' ? 'other_supplier' : 'other_customer';
        let failedCount = 0;
        let firstMessage: string | null = null;
        for (const file of data.files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('po', String(line.po));
          formData.append('po_line', String(line.id));
          formData.append('document_type', docType);
          formData.append(
            'description',
            `Change request attachment (${data.action}): ${file.name}`,
          );
          try {
            await purchasingAPI.uploadPODocument(formData);
          } catch (uploadErr: unknown) {
            failedCount += 1;
            console.error('Failed to upload attachment:', uploadErr);
            if (!firstMessage) {
              firstMessage = getApiErrorMessage(uploadErr, 'Upload failed');
            }
          }
        }
        if (failedCount > 0) {
          toast.error(
            failedCount === data.files.length
              ? `Attachments could not be uploaded: ${firstMessage ?? 'Unknown error'}`
              : `${failedCount} of ${data.files.length} attachments failed. ${firstMessage ?? ''}`,
          );
        }
      }

    } catch (error: unknown) {
      console.error('Workflow action failed:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      const errorMessage = axiosError.response?.data?.message || 'Workflow action failed';
      toast.error(errorMessage);
      throw error;
    }

    // CR-4860: Side effects must not propagate to the caller. Previously,
    // fetchLineDetails / onUpdate ran inside the try block above; if the
    // action API succeeded (success toast already fired) but the post-call
    // refetch errored, the throw bubbled up to the caller. ProposeChangeModal
    // would catch it and skip its own onClose(), leaving the modal open
    // even though the propose itself succeeded. Run the refresh outside
    // the try, swallow refetch errors, so the propose flow can close.
    // CR-4883: also swallow errors from the parent's onUpdate callback —
    // if onUpdate throws (e.g. parent re-render quirk), the throw used to
    // bubble back to ProposeChangeModal and skip its own onClose, so the
    // modal stayed open even though the propose itself succeeded.
    try {
      await fetchLineDetails();
    } catch (refetchErr) {
      console.warn('Workflow action succeeded but refetch failed:', refetchErr);
    }
    try {
      onUpdate?.();
    } catch (updateErr) {
      console.warn('Workflow action succeeded but onUpdate failed:', updateErr);
    }
  };

  // CR-4203: Check if workflow actions should be shown
  const canShowWorkflowActions = (): boolean => {
    if (!line || !user || !user.platform) return false;
    // CR-4697: No actions on closed (fully received) PO lines
    if (line.status === 'received') return false;
    // Schedule-aware tenants negotiate at the schedule level only.
    if (scheduleCollaborationActive) return false;

    const status = line.acknowledge_status;
    const isCustomer = user.platform === 'CUSTOMER' || user.platform === 'HELIX';
    const isSupplier = user.platform === 'SUPPLIER';

    // CR-4647: Site-based write access only applies to customer users.
    // Suppliers always have write access to their own PO lines.
    if (isCustomer && !hasPurchasingWriteAccess(line.customer_id)) return false;

    if (isCustomer) {
      switch (status) {
        case 'change_proposed_supplier':
          return true;
        case 'change_declined':
          return line.previous_acknowledge_status === 'change_proposed_supplier';
        default:
          return false;
      }
    }

    if (isSupplier) {
      switch (status) {
        case 'new':
        case 'change_proposed_customer':
          return true;
        case 'change_declined':
          return line.previous_acknowledge_status === 'change_proposed_customer';
        default:
          return false;
      }
    }

    return false;
  };

  // CR-4203: Check if propose change should be shown
  const canShowProposeChange = (): boolean => {
    if (!line || !user || !user.platform) return false;
    // CR-4697: No actions on closed (fully received) PO lines
    if (line.status === 'received') return false;
    // Schedule-aware tenants negotiate at the schedule level only.
    if (scheduleCollaborationActive) return false;

    const status = line.acknowledge_status;
    const isCustomer = user.platform === 'CUSTOMER' || user.platform === 'HELIX';
    const isSupplier = user.platform === 'SUPPLIER';

    // CR-4647: Site-based write access only applies to customer users.
    if (isCustomer && !hasPurchasingWriteAccess(line.customer_id)) return false;

    if (isCustomer) {
      switch (status) {
        case 'new':
        case 'accepted':
        case 'wip':
          return true;
        case 'change_declined':
          return line.previous_acknowledge_status === 'change_proposed_customer';
        default:
          return false;
      }
    }

    if (isSupplier) {
      switch (status) {
        case 'new':
        case 'accepted':
        case 'wip':
          return true;
        case 'change_declined':
          return line.previous_acknowledge_status === 'change_proposed_supplier';
        default:
          return false;
      }
    }

    return false;
  };

  return {
    line,
    changelogs,
    loading,
    error,
    grnCount,
    ncrs,
    fetchLineDetails,
    getChangeCount,
    handleToggleCritical,
    handleToggleHold,
    handleWorkflowAction,
    canShowWorkflowActions,
    canShowProposeChange,
  };
}
